import { Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { EntitySelect } from '@/components/EntitySelect';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SKILLS_BASE } from '../api';
import { CabinetEmptyState, CabinetListFooter, CabinetListToolbar } from '../components/listChrome';
import { createCabinetViewStore, viewModes } from '../store/cabinetView';
import {
  useCabinetSkillCategories,
  useCabinetSkills,
  useCreateCabinetSkill,
  useDeleteCabinetSkill,
  useSetCabinetSkillCategory,
  useUpdateCabinetSkill,
} from '../queries';
import { createCabinetSkillOption, searchCabinetSkills } from '../skillSelect';
import type { CabinetEmployeeSkill } from '../types';

const useSkillsUi = createCabinetViewStore('cabinet.skills:ui', 'table');
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
type SkillColumn = 'skill__name' | 'level';
const LEVELS = [1, 2, 3, 4, 5];

export function CabinetSkillsPage() {
  const view = useSkillsUi((s) => s.view);
  const setView = useSkillsUi((s) => s.setView);
  const ordering = useSkillsUi((s) => s.ordering);
  const setOrdering = useSkillsUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCabinetSkills({
    page,
    search: search || undefined,
    ordering,
  });
  const create = useCreateCabinetSkill();
  const update = useUpdateCabinetSkill();
  const remove = useDeleteCabinetSkill();
  const setCategory = useSetCabinetSkillCategory();
  const { data: categoryOptions = [] } = useCabinetSkillCategories();

  const [skillId, setSkillId] = useState('');
  const [level, setLevel] = useState('3');

  const rows = data?.results ?? [];

  async function handleAdd() {
    if (!skillId) return;
    await create.mutateAsync({ skill: Number(skillId), level: Number(level) });
    setSkillId('');
    setLevel('3');
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Skills</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add a skill</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-64">
            <p className="mb-1 text-xs font-medium uppercase">Skill</p>
            <EntitySelect
              queryKey="cabinet-skill-picker"
              value={skillId}
              onChange={setSkillId}
              search={searchCabinetSkills}
              onCreate={createCabinetSkillOption}
              placeholder="Pick or type a skill"
              searchPlaceholder="Search or add a skill…"
              createLabel={(q) => `Add "${q}"`}
            />
          </div>
          <div className="w-28">
            <p className="mb-1 text-xs font-medium uppercase">Level</p>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEVELS.map((l) => (
                  <SelectItem key={l} value={String(l)}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleAdd} disabled={!skillId || create.isPending}>
            Add
          </Button>
        </CardContent>
      </Card>

      <CabinetListToolbar
        view={view}
        onViewChange={setView}
        modes={MODES}
        search={{
          value: searchInput,
          onChange: (v) => {
            setPage(1);
            setSearchInput(v);
          },
          placeholder: 'Search skill…',
        }}
        exportBase={SKILLS_BASE}
        exportParams={{ search: search || undefined, ordering }}
      />

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <SkillsTable
          rows={rows}
          ordering={ordering}
          onOrderingChange={setOrdering}
          onLevel={(id, lvl) => update.mutate({ id, input: { level: lvl } })}
          onCategory={(skillId, category) => setCategory.mutate({ skillId, category })}
          categoryOptions={categoryOptions}
          onRemove={(id) => remove.mutate(id)}
          removePending={remove.isPending}
        />
      ) : view === 'list' ? (
        <SkillsList rows={rows} />
      ) : view === 'cards' ? (
        <SkillsCards rows={rows} />
      ) : (
        <SkillsGroupedByCategory rows={rows} />
      )}

      <CabinetListFooter
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        hasPrev={Boolean(data?.previous)}
        hasNext={Boolean(data?.next)}
        isFetching={isFetching}
        noun={['skill', 'skills']}
      />
    </div>
  );
}

interface SkillsTableProps {
  rows: CabinetEmployeeSkill[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  onLevel: (id: number, level: number) => void;
  onCategory: (skillId: number, category: string) => void;
  categoryOptions: string[];
  onRemove: (id: number) => void;
  removePending: boolean;
}

const CATEGORY_DATALIST_ID = 'cabinet-skill-categories';

function SkillsTable({
  rows,
  ordering,
  onOrderingChange,
  onLevel,
  onCategory,
  categoryOptions,
  onRemove,
  removePending,
}: SkillsTableProps) {
  return (
    <div className="rounded-md border">
      {categoryOptions.length > 0 ? (
        <datalist id={CATEGORY_DATALIST_ID}>
          {categoryOptions.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<SkillColumn> column="skill__name" value={ordering} onChange={onOrderingChange}>
              Skill
            </SortableTableHead>
            <TableHead className="hidden sm:table-cell">Category</TableHead>
            <SortableTableHead<SkillColumn>
              column="level"
              value={ordering}
              onChange={onOrderingChange}
              className="w-32"
            >
              Level
            </SortableTableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                No skills added yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{s.skill_name}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  <CategoryCell
                    value={s.skill_category ?? ''}
                    onSave={(category) => onCategory(s.skill, category)}
                    listId={categoryOptions.length > 0 ? CATEGORY_DATALIST_ID : undefined}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={String(s.level)}
                    onValueChange={(v) => onLevel(s.id, Number(v))}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((l) => (
                        <SelectItem key={l} value={String(l)}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label={`Remove ${s.skill_name}`}>
                        <Trash2 />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove {s.skill_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes the skill from your matrix.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onRemove(s.id)} disabled={removePending}>
                          Remove
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/** Inline category editor: click the value (or "Add category" when empty) to
 *  edit; Enter or blur saves, Escape cancels. Edits the shared catalog skill. */
function CategoryCell({
  value,
  onSave,
  listId,
}: {
  value: string;
  onSave: (category: string) => void;
  listId?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (!editing) {
    return value ? (
      <button
        type="button"
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        className="hover:bg-accent/40 text-muted-foreground -mx-1 rounded px-1 text-left"
      >
        {value}
      </button>
    ) : (
      <button
        type="button"
        onClick={() => {
          setDraft('');
          setEditing(true);
        }}
        className="text-muted-foreground/60 hover:text-foreground text-sm"
      >
        + Add category
      </button>
    );
  }

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== value) onSave(next);
  };

  return (
    <Input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setEditing(false);
        }
      }}
      list={listId}
      placeholder="Category"
      className="h-8 w-36"
      aria-label="Skill category"
    />
  );
}

function SkillsList({ rows }: { rows: CabinetEmployeeSkill[] }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((s) => (
        <li key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-semibold">{s.skill_name}</span>
          <span className="text-muted-foreground hidden sm:inline">{s.skill_category || ''}</span>
          <Badge variant="outline" className="shrink-0">
            Level {s.level}
          </Badge>
        </li>
      ))}
    </ul>
  );
}

function SkillsCards({ rows }: { rows: CabinetEmployeeSkill[] }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((s) => (
        <Card key={s.id}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-base">
              {s.skill_name}
              <Badge variant="outline">Level {s.level}</Badge>
            </CardTitle>
          </CardHeader>
          {s.skill_category ? (
            <CardContent className="text-muted-foreground text-xs">{s.skill_category}</CardContent>
          ) : null}
        </Card>
      ))}
    </div>
  );
}

function SkillsGroupedByCategory({ rows }: { rows: CabinetEmployeeSkill[] }) {
  const groups = useMemo(() => {
    const byCat = new Map<string, CabinetEmployeeSkill[]>();
    for (const s of rows) {
      const cat = s.skill_category || 'Uncategorized';
      (byCat.get(cat) ?? byCat.set(cat, []).get(cat)!).push(s);
    }
    return [...byCat.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-6">
      {groups.map(([cat, bucket]) => (
        <section key={cat}>
          <h2 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
            {cat}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <SkillsCards rows={bucket} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>No skills added yet.</CabinetEmptyState>;
}
